import { stringEmptyDefault } from '@common/helpers';
import { PassengerType, ServicingCommonModel } from '@common/interfaces';
import { MybookingServiceState } from '@common/interfaces/mybooking/mybooking-service.interface';
import {
  ChargeableLoungePassengerApplyStatusMap,
  FirstBaggageBoundMap,
  FirstBaggagePassengerApplyInfo,
  MealPassengerApplyInfo,
  MealPassengerApplyStatusMap,
  ServiceInfoData,
} from '@common/interfaces/mybooking/mybooking.interface';
import { SeatNumbers } from '@common/interfaces/servicing-seatmap/seat-info';
import {
  Traveler,
  UpdateServicesRequestSeats,
  UpdateServicesRequestSeatsChildSeatsInner,
  UpdateServicesRequestSeatsSeatReservationInner,
  UpdateServicesRequestSeatsSeatReservationInnerAttributeRequest,
  UpdateServicesRequestSeatsSeatReservationInnerSeatSelectionInner,
  UpdateServicesRequestServicesInner,
  UpdateServicesRequestServicesInnerSegmentsInnerTravelersInner,
  UpdateServicesRequestServicesInnerSegmentsInnerTravelersInnerSpecialServiceRequestsInner,
} from 'src/sdk-servicing';
import { isEmptyArray, isEmptyObject, isStringEmpty } from '../utilities/format';
import { SegmentMap } from '@common/interfaces/servicing-seatmap/seat-reservation-status';
import { TravelerMealInfo } from '@common/interfaces/mybooking/order-services.interface';
import { isEmpty } from 'rxjs';
export interface ParamNo12 {
  myBookingServiceInformation: ServicingCommonModel;
  mybookingServiceState: MybookingServiceState;
}
// (共通No12) サービス情報登録APIのデータ用意処理
export function onHandleCommonNo12({ myBookingServiceInformation, mybookingServiceState }: ParamNo12): ServiceInfoData {
  // (12.1.1)	サービス情報データ
  let serviceInfoData: ServiceInfoData = {
    services: [],
    seatReservationList: [],
    seats: {},
  };
  // (12.1.2) サービス申込情報リスト
  let services: Array<UpdateServicesRequestServicesInner> = [];
  // (12.1.3) 座席指定セグメントリスト
  let seatReservationList: string[] = [];
  // (12.1.4)座席指定情報
  let seats: UpdateServicesRequestSeats = {};
  // (12.2.1)	サービシング共通store.事前座席指定.申込操作中フラグ = trueの場合、以下の処理を行う。
  if (myBookingServiceInformation.seatReservationStatus?.isApplicationProcessed) {
    const seatInfo = onProcessSeatReservationStatus(myBookingServiceInformation, mybookingServiceState);
    Object.assign(seats, seatInfo.seats);
    seatReservationList = [...seatReservationList, ...seatInfo.seatReservationList];
    services = [...services, ...seatInfo?.services];
  }

  // (12.2.2)	サービシング共通store.事前追加手荷物.申込操作中フラグ = trueの場合、以下の処理を行う。
  if (myBookingServiceInformation.firstBaggage?.isApplicationProcessed) {
    const firstBaggage = onProcessFirstBaggageInformation(myBookingServiceInformation, mybookingServiceState);
    services = [...services, ...firstBaggage];
  }

  // (12.2.3)	サービシング共通store.ラウンジ情報.申込操作中フラグ = trueの場合、以下の処理を行う。
  if (myBookingServiceInformation.chargeableLounge?.isApplicationProcessed) {
    services = [
      ...services,
      ...onProcessChargeableLoungeInformation(myBookingServiceInformation, mybookingServiceState),
    ];
  }

  // (12.2.4) サービシング共通store.機内食情報.申込操作中フラグ = trueの場合、以下の処理を行う。
  if (myBookingServiceInformation.meal?.isApplicationProcessed) {
    services = [...services, ...onProcessMealInformation(myBookingServiceInformation, mybookingServiceState)];
  }
  // (12.3)	サービス情報データを設定する。
  serviceInfoData.services = services;
  serviceInfoData.seatReservationList = seatReservationList;
  serviceInfoData.seats = seats;
  return serviceInfoData;
}

export function onProcessFirstBaggageInformation(
  myBookingServiceInformation: ServicingCommonModel,
  mybookingServiceState: MybookingServiceState
): Array<UpdateServicesRequestServicesInner> {
  let services: Array<UpdateServicesRequestServicesInner> = [];
  // ＜以下、事前追加手荷物バウンド情報リストの要素数分繰り返し＞(それぞれ要素が当該事前追加手荷物バウンド情報)
  mybookingServiceState.firstBaggageBoundInforList?.forEach((bound) => {
    // (12.2.2.1)	サービシング共通store.事前追加手荷物.バウンドマップ.<当該事前追加手荷物バウンド情報.airBoundId>を取得し、バウンドマップ情報とする
    const boundInfo = myBookingServiceInformation.firstBaggage?.[bound.airBoundId!] as FirstBaggageBoundMap;

    // (12.2.2.2) 搭乗者情報リスト
    const travelers: Array<any> = [];

    //＜以下、事前追加手荷物搭乗者情報リストの要素数分繰り返し＞(それぞれ要素が当該事前追加手荷物搭乗者情報)
    mybookingServiceState.firstBaggagePassengerInforList?.forEach((traveler) => {
      // (12.2.2.3) バウンドマップ情報.搭乗者別申込状況マップ.<当該事前追加手荷物搭乗者情報.搭乗者ID>を取得し、事前追加手荷物申込状況マップとする
      const travelerInfo = boundInfo?.[traveler.id!] as FirstBaggagePassengerApplyInfo;
      // (12.2.2.4) 事前追加手荷物申込状況マップが存在する場合、
      if (!isEmptyObject(travelerInfo)) {
        // (12.2.2.4.1) 以下の通りに搭乗者情報オブジェクトを作成する。
        const travelerObj = {
          travelerId: traveler.id ?? '',
          specialServiceRequests: [
            {
              code: 'FBAG',
              operation: travelerInfo.updateCategory,
            },
          ],
        };
        // (12.2.2.4.2) 搭乗者情報リストに作成した搭乗者情報オブジェクトを追加する。
        travelers.push(travelerObj);
      }
    });
    // (12.2.2.5)	搭乗者情報リスト≧1件の場合、以下の処理を行う。
    if (!isEmptyArray(travelers)) {
      // (12.2.2.5.1)	以下のパラメータで「当画面共通処理No20.バウンドID取得処理」を行って、バウンドIDを取得する。
      const boundFound = mybookingServiceState.pnr?.air?.bounds?.find((item) => item.airBoundId === bound.airBoundId);
      const segments =
        boundFound?.flights?.map((flight) => {
          return {
            segmentId: flight.id!,
            travelers: travelers,
          };
        }) ?? [];
      const service: UpdateServicesRequestServicesInner = {
        airBoundId: bound.airBoundId!,
        segments: segments,
      };
      // (12.2.2.5.2) サービス申込情報リストに作成したサービス申込情報オブジェクトを追加する。
      services.push(service);
    }
  });
  return services;
}
export function onProcessChargeableLoungeInformation(
  myBookingServiceInformation: ServicingCommonModel,
  mybookingServiceState: MybookingServiceState
): Array<UpdateServicesRequestServicesInner> {
  let services: Array<any> = [];
  // ＜以下、ラウンジセグメント情報リストの要素数分繰り返し＞(それぞれ要素が当該ラウンジセグメント情報)
  mybookingServiceState.loungeFlightInforList?.forEach((flight) => {
    // (12.2.3.1)	サービシング共通store.ラウンジ情報ラウンジ情報.セグメントマップ.<当該ラウンジセグメント情報.セグメントID>を取得し、セグメントマップ情報とする。
    const segmentInfo = myBookingServiceInformation.chargeableLounge?.[
      flight.id!
    ] as ChargeableLoungePassengerApplyStatusMap;

    // (12.2.3.2) ラウンジサービス申込情報マップ、
    let loungeServiceInformation: any = {};
    // (12.2.3.2) 搭乗者情報リストを空で作成する。
    const travelers: Array<any> = [];

    // ＜以下、ラウンジ搭乗者情報リストの要素数分繰り返し＞(それぞれ要素が当該ラウンジ搭乗者情報)
    mybookingServiceState.loungePassengerInforList?.forEach((traveler) => {
      // (12.2.3.3) セグメントマップ情報.搭乗者別申込状況マップ.<当該ラウンジ搭乗者情報.搭乗者ID>を取得し、ラウンジ申込状況マップとする。
      const loungeApplicationInfo = segmentInfo?.[traveler.id!];

      // (12.2.3.4) ラウンジ申込状況マップが存在する場合、
      if (!isEmptyObject(loungeApplicationInfo)) {
        // (12.2.3.4.2) 搭乗者情報リストに作成した搭乗者情報オブジェクトを追加する。
        const travelerObj: any = {
          travelerId: traveler.id ?? '',
          specialServiceRequests: [
            {
              code: loungeApplicationInfo?.ssrCode ?? '',
              operation: loungeApplicationInfo?.updateCategory,
            },
          ],
        };
        travelers.push(travelerObj);
      }
    });
    // (12.2.3.5) 搭乗者情報リスト≧1件の場合、以下の処理を行う。.
    if (!isEmptyArray(travelers)) {
      // (12.2.3.5.1)	以下のパラメータで「当画面共通処理No20.バウンドID取得処理」を行って、バウンドIDを取得する。
      const boundId = onHandleCommonNo20(mybookingServiceState, flight.id!);
      // (12.2.3.5.2) ラウンジサービス申込情報マップに取得したバウンドIDが存在しない場合
      if (!loungeServiceInformation[boundId]) {
        // (12.2.3.5.2.1) 以下の通りにサービス申込情報オブジェクトを作成する。
        loungeServiceInformation[boundId] = {
          airBoundId: boundId,
          segments: [
            {
              segmentId: flight.id ?? '',
              travelers: travelers,
            },
          ],
        };
      } else {
        // (12.2.3.5.3.1)	以下の通りにセグメント情報オブジェクトを作成する。
        loungeServiceInformation[boundId].segments.push({
          segmentId: flight.id ?? '',
          travelers: travelers,
        });
      }
      // (12.2.3.5.2.2) キー = バウンドID、値 = サービス申込情報オブジェクトである情報をラウンジサービス申込情報マップに追加する。
      // (12.2.3.5.3.2)	ラウンジサービス申込情報マップ<バウンドID>.サービス申込情報オブジェクト.セグメント情報に上記で作成したセグメント情報オブジェクトを追加する。
      //  services.push(loungeServiceInformation[boundId]);
      Object.values(loungeServiceInformation).forEach((value) => {
        // (12.2.3.6)	サービス申込情報リストに当該ラウンジサービス申込情報マップ.サービス申込情報オブジェクトを追加する。
        services.push(value);
      });
    }
  });
  return services;
}

export function onProcessMealInformation(
  myBookingServiceInformation: ServicingCommonModel,
  mybookingServiceState: MybookingServiceState
): Array<UpdateServicesRequestServicesInner> {
  let services: Array<any> = [];
  // ＜以下、機内食セグメント情報リストの要素数分繰り返し＞(それぞれ要素が当該機内食セグメント情報)
  mybookingServiceState.mealFlightInforList?.forEach((flight) => {
    // (12.2.4.1) サービシング共通store.機内食情報.セグメントマップ.<当該機内食セグメント情報.セグメントID>を取得し、セグメントマップ情報とする。
    const segmentInfo = myBookingServiceInformation.meal?.[flight.id!] as MealPassengerApplyStatusMap;
    // (12.2.4.2) 機内食サービス申込情報マップ
    let inFlightServiceApplication: any = {};
    // (12.2.4.2) 搭乗者情報リストを空で作成する。
    let travelers: Array<any> = [];

    // ＜以下、機内食搭乗者情報リストの要素数分繰り返し＞(それぞれ要素が当該機内食搭乗者情報)
    mybookingServiceState.mealPassengerInforList?.forEach((traveler) => {
      // (12.2.4.3) セグメントマップ情報.搭乗者別申込状況マップ.<当該機内食搭乗者情報.搭乗者ID>を取得し、機内食申込状況マップとする。
      const mealApplicationInfo = segmentInfo?.[traveler.id!] as MealPassengerApplyInfo;

      // (12.2.4.4) 機内食申込状況マップが存在する場合、
      if (!isEmptyObject(mealApplicationInfo)) {
        // (12.2.4.4.1)	当該機内食搭乗者情報.搭乗者種別　＝　INF　かつ　機内食申込状況マップ.SSRコード　＝　BBML　かつ　機内食申込状況マップ.更新区分　＝　requestの 場合
        if (
          traveler?.passengerTypeCode === PassengerType.INF &&
          mealApplicationInfo?.ssrCode === TravelerMealInfo.MealCode.BBML &&
          (mealApplicationInfo?.updateCategory === TravelerMealInfo.UpdateCategory.REQUEST ||
            mealApplicationInfo?.updateCategory === TravelerMealInfo.UpdateCategory.CANCEL)
        ) {
          const existPassengerIdInPassengerInforList = travelers?.some((_traveler) => {
            return _traveler?.travelerId === traveler?.accompanyingTravelerId;
          });
          // (12.4.4.1.1)	搭乗者情報リストに当該機内食搭乗者情報.同伴する搭乗者IDが含まれている場合
          if (existPassengerIdInPassengerInforList) {
            // (12.2.4.4.1.1.1)	搭乗者情報リスト内の同伴する搭乗者IDと合致するオブジェクトに対して下記を追加する
            travelers = travelers.map((travelerInfo) => {
              if (travelerInfo?.travelerId === traveler?.accompanyingTravelerId) {
                return {
                  ...travelerInfo,
                  specialServiceRequests: [
                    ...travelerInfo.specialServiceRequests,
                    {
                      code: mealApplicationInfo.ssrCode,
                      operation: mealApplicationInfo.updateCategory,
                    },
                  ],
                };
              }

              return travelerInfo;
            });
          }
          // (12.2.4.4.1.2)	搭乗者情報リストに当該機内食搭乗者情報.同伴する搭乗者IDが含まれていない場合
          else {
            // (12.2.4.4.1.2.1)	以下の通りに搭乗者情報オブジェクトを作成する。
            const travelerObj = {
              travelerId: traveler.accompanyingTravelerId,
              specialServiceRequests: [
                {
                  code: mealApplicationInfo.ssrCode,
                  operation: mealApplicationInfo.updateCategory,
                },
              ],
            };
            // (12.2.4.4.1.2.2)	搭乗者情報リストに作成した搭乗者情報オブジェクトを追加する。
            travelers.push(travelerObj);
          }
        }
        // (12.2.4.4.2)	上記以外の場合
        else {
          const existPassengerIdInPassengerInforList = travelers?.some((_traveler) => {
            return _traveler?.travelerId === traveler?.id;
          });
          // (12.2.4.4.2.1)	搭乗者情報リストに当該機内食搭乗者情報.搭乗者IDが含まれている場合
          if (existPassengerIdInPassengerInforList) {
            // (12.2.4.4.2.1.1)	搭乗者情報リスト内の搭乗者IDと合致するオブジェクトに対して下記を追加する
            travelers = travelers.map((travelerInfo) => {
              if (travelerInfo?.travelerId === traveler?.id) {
                return {
                  ...travelerInfo,
                  specialServiceRequests: [
                    ...travelerInfo.specialServiceRequests,
                    {
                      code: mealApplicationInfo?.ssrCode ?? '',
                      operation: mealApplicationInfo?.updateCategory ?? '',
                    },
                  ],
                };
              }

              return travelerInfo;
            });
          }
          // (12.2.4.4.2.2)	搭乗者情報リストに当該機内食搭乗者情報.搭乗者IDが含まれていない場合
          else {
            // (12.2.4.4.2.2.1)	以下の通りに搭乗者情報オブジェクトを作成する。
            const travelerObj = {
              travelerId: traveler.id ?? '',
              specialServiceRequests: [
                {
                  code: mealApplicationInfo.ssrCode ?? '',
                  operation: mealApplicationInfo.updateCategory,
                },
              ],
            };
            // (12.2.4.4.2.2.2)	搭乗者情報リストに作成した搭乗者情報オブジェクトを追加する。
            travelers.push(travelerObj);
          }
        }
      }
    });

    // (12.2.4.5) 搭乗者情報リスト≧1件の場合、以下の処理を行う。
    if (!isEmptyArray(travelers)) {
      // (12.2.4.5.1)	以下のパラメータで「当画面共通処理No20.バウンドID取得処理」を行って、バウンドIDを取得する。
      const boundId = onHandleCommonNo20(mybookingServiceState, flight.id!);
      // (12.2.4.5.2)	機内食サービス申込情報マップに取得したバウンドIDが存在しない場合
      if (!inFlightServiceApplication[boundId]) {
        // (12.2.4.5.2.1) 以下の通りにサービス申込情報オブジェクトを作成する。

        inFlightServiceApplication[boundId] = {
          airBoundId: boundId,
          segments: [
            {
              segmentId: flight.id ?? '',
              travelers: travelers,
            },
          ],
        };
      } else {
        // (12.2.4.5.3) 機内食サービス申込情報マップに取得したバウンドIDが存在した場合
        inFlightServiceApplication[boundId].segments.push({
          segmentId: flight.id ?? '',
          travelers: travelers,
        });
      }

      Object.values(inFlightServiceApplication).forEach((value) => {
        // (12.2.3.6)	サービス申込情報リストに当該ラウンジサービス申込情報マップ.サービス申込情報オブジェクトを追加する。
        services.push(value);
      });
    }
  });
  return services;
}

export function onProcessSeatReservationStatus(
  myBookingServiceInformation: ServicingCommonModel,
  mybookingServiceState: MybookingServiceState
) {
  let seats: UpdateServicesRequestSeats = {};
  let seatReservationList: string[] = [];
  const services: UpdateServicesRequestServicesInner[] = [];
  // (12.2.1.1)	事前座席指定リストを空で作成する。
  const seatReservation: Array<UpdateServicesRequestSeatsSeatReservationInner> = [];
  // (12.2.1.1.2)	チャイルドシート申込リストを空で作成する。
  const childSeats: Array<UpdateServicesRequestSeatsChildSeatsInner> = [];
  // ＜以下、事前座席指定セグメント情報リストの要素数分繰り返し＞(それぞれ要素が当該セグメント情報)
  mybookingServiceState.seatFlightInforList?.forEach((flight) => {
    // (12.2.1.2) カウチ席申込リストを空で作成する。
    const couchSeatList: UpdateServicesRequestServicesInnerSegmentsInnerTravelersInner[] = [];
    // (12.2.1.3). 事前座席指定バウンドIDを空で作成する。
    let airBoundId = '';
    // (12.2.1.4)	サービシング共通store.事前座席指定.セグメントマップ.<当該セグメント情報.セグメントID>を取得し、セグメントマップ情報とする
    // セグメントマップ情報
    // (12.2.1.6)	セグメントマップ情報.seatAttribute≠""の場合、
    let firstAttributeNotEmpty = '';

    const flightOfSeat = (myBookingServiceInformation.seatReservationStatus?.[String(flight?.id)] as SegmentMap) ?? {};
    let flightOfCouchSeat: SegmentMap = {};
    Object.entries(flightOfSeat).forEach(([k, value]) => {
      const couchSeat = value?.couchSeatNumber?.[0];
      let isDuplicate = false;
      Object.values(flightOfCouchSeat).forEach((newObj) => {
        const newCouchSeat = newObj?.couchSeatNumber?.[0];
        if (!isStringEmpty(newCouchSeat?.seatNumber) && newCouchSeat?.seatNumber === couchSeat?.seatNumber) {
          isDuplicate = true;
        }
      });
      // (12.2.1.5) セグメントマップ情報.couchSeatNumber.seatNumber[]に同じ要素(空文字を除く)が存在する、または(セグメントマップ情報.updateCategory="cancel"、かつセグメントマップ情報.ssrCode≠""(空欄))場合、、
      // (12.2.1.5.1)	seatNumberが一つのみを取得するかつ搭乗者ID[0]を取得する。
      // (12.2.1.5.2)	以下の通りにカウチ情報オブジェクトを作成する
      //( 12.2.1.5.3)	カウチ席申込リストに作成したカウチ情報オブジェクトを追加する。
      // (12.2.1.5.4)	事前座席指定バウンドIDにセグメントマップ情報.バウンドIDを設定する。

      if (
        isDuplicate === false &&
        (!isStringEmpty(couchSeat?.seatNumber) ||
          (value?.updateCategory === 'cancel' && !isStringEmpty(value?.ssrCode)))
      ) {
        flightOfCouchSeat[k] = value;
        couchSeatList.push({
          travelerId: stringEmptyDefault(k),
          specialServiceRequests: [
            {
              code: stringEmptyDefault(value?.ssrCode),
              operation:
                value?.updateCategory as UpdateServicesRequestServicesInnerSegmentsInnerTravelersInnerSpecialServiceRequestsInner.OperationEnum,
            },
          ],
        });
        airBoundId = stringEmptyDefault(value?.airBoundId);
      }
      // (12.2.1.6.1)	seatAttributeが一つのみを取得するかつ搭乗者ID[0]を取得する。
      // (12.2.1.6.2)	以下の通りに事前座席指定情報オブジェクトを作成する。
      if (!isStringEmpty(value?.seatAttribute) && isStringEmpty(firstAttributeNotEmpty)) {
        firstAttributeNotEmpty = stringEmptyDefault(value?.seatAttribute);
        seatReservation.push({
          flightId: flight.id ?? '',
          attributeRequest: {
            type: firstAttributeNotEmpty as UpdateServicesRequestSeatsSeatReservationInnerAttributeRequest.TypeEnum,
          },
        });
      }
    });

    // (12.2.1.7)	セグメントマップ情報.seatAttribute=""の場合、
    if (isStringEmpty(firstAttributeNotEmpty)) {
      // (12.2.1.8)	セグメントマップ情報.搭乗者別申込状況マップ.<当該搭乗者情報.搭乗者ID>を取得し、シートマップ申込状況マップとする
      mybookingServiceState.seatPassengerInforList?.forEach((traveler) => {
        // シートマップ申込状況マップ
        const isExistPassengerApplicationStatusMap = !isEmptyObject(flightOfSeat?.[String(traveler?.id)]);

        // (12.2.1.9) シートマップ申込状況マップが存在する場合、以下は事前座席指定情報の設定処理。
        if (isExistPassengerApplicationStatusMap) {
          const travelerOfSeat = flightOfSeat?.[String(traveler.id)];
          // (12.2.1.9.1)	以下の通りに事前座席指定情報オブジェクトを作成する。
          // (12.2.1.9.1)	座席番号リストに""(空欄)を作成する。
          let seatNumberList =
            (travelerOfSeat?.seatNumber?.length && travelerOfSeat?.seatNumber) ||
            (travelerOfSeat?.couchSeatNumber?.length && travelerOfSeat?.couchSeatNumber) ||
            [];

          let mapSeatInfo: UpdateServicesRequestSeatsSeatReservationInner = {
            flightId: flight.id ?? '',
            seatSelection: mapToNewSeatSelection(seatNumberList, traveler),
          };
          // (12.2.1.9.2)	作成した事前座席指定情報オブジェクトを事前座席指定リストに追加する。
          let index = -1;
          for (let i = 0; i < seatReservation.length; i++) {
            if (mapSeatInfo.flightId === seatReservation[i].flightId) {
              index = i;
            }
          }
          if (index < 0) {
            seatReservation.push(mapSeatInfo);
          } else {
            seatReservation[index].seatSelection?.push(...(mapSeatInfo.seatSelection ?? []));
          }
        }

        // (12.2.1.10)	座席指定セグメントリストに当該セグメント情報.セグメントIDを追加する。
        seatReservationList.push(flight.id!);
      });
      // (12.2.1.11)	カウチ席申込リストが1件以上の場合、以下の処理を行う。
      if (!isStringEmpty(airBoundId) && !isEmptyArray(couchSeatList)) {
        // (12.2.1.11.1).	以下の通りにサービス申込情報オブジェクトを作成する。
        services.push({
          airBoundId: airBoundId,
          segments: [
            {
              segmentId: stringEmptyDefault(flight?.id),
              travelers: couchSeatList,
            },
          ],
        });
      }
    }
  });
  // (12.2.1.12).	シートマップ申込状況マップ.チャイルドシート更新区分が存在する場合、以下の処理を行う。
  // (12.2.1.12.1).	以下の通りにチャイルドシート申込オブジェクトを作成する。
  myBookingServiceInformation?.seatReservationStatus?.childSeatInfoList?.forEach((seat) => {
    childSeats.push({
      travelerId: seat?.travelerId,
      operation: stringEmptyDefault(seat?.updateCategory) as UpdateServicesRequestSeatsChildSeatsInner.OperationEnum,
      type: stringEmptyDefault(seat?.childSeatType),
    });
  });
  // (12.2.1.13)	事前座席指定情報リストが1件以上　または、チャイルドシート申込リストが1件以上の場合、座席指定情報に以下の通り設定する。
  seats = {
    ...seats,
    ...(seatReservation.length >= 1 && { seatReservation: seatReservation }),
    ...(childSeats.length >= 1 && { childSeats: childSeats }),
  };

  // (12.2.1.12.2)	以下の通りにサービス申込情報オブジェクトを作成する。
  return {
    seats,
    seatReservationList,
    services,
  };
}

export function mapToNewSeatSelection(
  seatNumberList: Array<SeatNumbers>,
  traveler: Traveler
): Array<UpdateServicesRequestSeatsSeatReservationInnerSeatSelectionInner> {
  if (!isEmptyArray(seatNumberList)) {
    return seatNumberList.map((seat) => {
      return {
        seatNumber: seat.seatNumber ?? '',
        travelerId: traveler.id ?? '',
      };
    });
  } else {
    return [
      {
        seatNumber: '',
        travelerId: traveler.id ?? '',
      },
    ];
  }
}
export function onHandleCommonNo20(mybookingServiceState: MybookingServiceState, flightId: string) {
  // 1. バウンドIDに""(空欄)を設定する。
  let boundId = '';
  for (const bound of mybookingServiceState.pnr?.air?.bounds!) {
    for (const flight of bound.flights!) {
      // 2. 当該セグメント情報.id=入力パラメータ.セグメントIDの場合、バウンドIDに当該バウンド情報.airBoundIdを設定して、ループを終了する。
      if (flight?.id === flightId) {
        boundId = bound.airBoundId!;
        break;
      }
    }
  }
  // 3. バウンドIDを返却する。
  return boundId;
}
